// 사업장(A) + 의무 캘린더 + 교육(C) API 클라이언트

import { api } from './api'
import type {
  AppointmentIn, AppointmentOut, AttendanceOut, ComplianceEduCode,
  ComplianceRuleOut, CoverageMatrixOut, DocumentFileOut, DocumentIn, DocumentOut,
  ForecastOut, GuestAttendOut, GuestLookupOut, HazardIn, HazardOut,
  ObligationListOut, ObligationOut, PsmUpdateIn, PublicSessionInfo, RiskSummaryOut,
  RoadmapOut, SessionIn, SessionOut, SiteIn, SiteOut, UpcomingOut,
  WorkerIn, WorkerOut, WorkerWithHistory,
  EquipmentIn, EquipmentOut, EquipmentSummary,
} from '../types/sites'

// ── 사업장 ──────────────────────────────────────

export const sitesApi = {
  list: () => api.get<SiteOut[]>('/sites').then(r => r.data),
  get: (id: string) => api.get<SiteOut>(`/sites/${id}`).then(r => r.data),
  create: (data: SiteIn) => api.post<SiteOut>('/sites', data).then(r => r.data),
  // 미등록 사용자도 '오늘' 홈을 갖도록 최소 초안 사업장을 확보(없으면 자동 생성, 멱등).
  // 다중 사업장 플랜(enterprise)에서 사업장이 없으면 null 반환.
  ensureDefault: () => api.post<SiteOut | null>('/sites/ensure-default').then(r => r.data),
  update: (id: string, data: SiteIn) =>
    api.put<SiteOut>(`/sites/${id}`, data).then(r => r.data),
  updatePsm: (id: string, data: PsmUpdateIn) =>
    api.put<SiteOut>(`/sites/${id}/psm`, data).then(r => r.data),
  // 세이프웨더 등 지역 기반 기능용 — 시·도/시·군·구만 간편 등록·수정
  updateAddress: (id: string, data: { sido?: string; sigungu?: string }) =>
    api.put<SiteOut>(`/sites/${id}/address`, data).then(r => r.data),
  remove: (id: string) => api.delete(`/sites/${id}`).then(() => undefined),

  // 선임
  listAppointments: (siteId: string) =>
    api.get<AppointmentOut[]>(`/sites/${siteId}/appointments`).then(r => r.data),
  createAppointment: (siteId: string, data: AppointmentIn) =>
    api.post<AppointmentOut>(`/sites/${siteId}/appointments`, data).then(r => r.data),
  updateAppointment: (siteId: string, apptId: string, data: AppointmentIn) =>
    api.put<AppointmentOut>(`/sites/${siteId}/appointments/${apptId}`, data).then(r => r.data),
  deleteAppointment: (siteId: string, apptId: string) =>
    api.delete(`/sites/${siteId}/appointments/${apptId}`).then(() => undefined),

  /** 자격증 사본 — 관서 신고 시 함께 제출해야 하는 증명서류. 업로드 경로가 아예 없었다. */
  uploadAppointmentCert: (siteId: string, apptId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post<AppointmentOut>(`/sites/${siteId}/appointments/${apptId}/cert`, fd, {
      timeout: 120_000, headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data)
  },

  /** 선임서 PDF — 지금까지 이 서류를 만들어 주지 않아 선임 자체를 증명할 수 없었다. */
  appointmentPdf: (siteId: string, apptId: string) =>
    api.get(`/sites/${siteId}/appointments/${apptId}/appointment.pdf`,
            { responseType: 'blob', timeout: 60_000 }).then(r => r.data as Blob),

  /** 선임 현황표 PDF (근로감독 제출용 한 장) */
  appointmentsRosterPdf: (siteId: string) =>
    api.get(`/sites/${siteId}/appointments-roster.pdf`,
            { responseType: 'blob', timeout: 60_000 }).then(r => r.data as Blob),

  /** 증빙 문서 회차 이력 — 주기 의무는 회차별 증빙이 각각 필요하다(§164). */
  listDocumentFiles: (siteId: string, docType: string) =>
    api.get<DocumentFileOut[]>(`/sites/${siteId}/documents/${docType}/files`).then(r => r.data),

  downloadDocumentFile: (siteId: string, fileId: string) =>
    api.get(`/sites/${siteId}/documents/files/${fileId}`,
            { responseType: 'blob', timeout: 60_000 }).then(r => r.data as Blob),

  /** 법정 보존기한이 남아 있으면 서버가 409로 거부한다(그게 정상이다). */
  deleteDocumentFile: (siteId: string, fileId: string) =>
    api.delete(`/sites/${siteId}/documents/files/${fileId}`).then(() => undefined),

  // 문서
  listDocuments: (siteId: string) =>
    api.get<DocumentOut[]>(`/sites/${siteId}/documents`).then(r => r.data),
  upsertDocuments: (siteId: string, items: DocumentIn[]) =>
    api.put<DocumentOut[]>(`/sites/${siteId}/documents`, items).then(r => r.data),
  deleteDocument: (siteId: string, docId: string) =>
    api.delete(`/sites/${siteId}/documents/${docId}`).then(() => undefined),
  // 증빙 파일 첨부 — **회차로 누적된다**(덮어쓰지 않는다).
  // performedOn: 이 회차의 실시일(측정일·교육일). 안 주면 오늘로 잡는다.
  uploadDocumentFile: (siteId: string, docType: string, file: File, performedOn?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    if (performedOn) fd.append('performed_on', performedOn)
    return api.post<DocumentOut>(
      `/sites/${siteId}/documents/${encodeURIComponent(docType)}/file`, fd,
      { timeout: 120_000, headers: { 'Content-Type': 'multipart/form-data' } },
    ).then(r => r.data)
  },
  documentFileUrl: (siteId: string, docType: string) =>
    `/api/sites/${siteId}/documents/${encodeURIComponent(docType)}/file`,

  // 유해인자
  listHazards: (siteId: string) =>
    api.get<HazardOut[]>(`/sites/${siteId}/hazards`).then(r => r.data),
  createHazard: (siteId: string, data: HazardIn) =>
    api.post<HazardOut>(`/sites/${siteId}/hazards`, data).then(r => r.data),
  updateHazard: (siteId: string, id: string, data: HazardIn) =>
    api.put<HazardOut>(`/sites/${siteId}/hazards/${id}`, data).then(r => r.data),
  deleteHazard: (siteId: string, id: string) =>
    api.delete(`/sites/${siteId}/hazards/${id}`).then(() => undefined),

  // ── 장비·보호구 대장 ──
  listEquipment: (siteId: string, params?: { category?: string; cert_status?: string }) =>
    api.get<EquipmentOut[]>(`/sites/${siteId}/equipment`, { params }).then(r => r.data),
  equipmentSummary: (siteId: string) =>
    api.get<EquipmentSummary>(`/sites/${siteId}/equipment/summary`).then(r => r.data),
  createEquipment: (siteId: string, data: EquipmentIn, autoVerify = true) =>
    api.post<EquipmentOut>(`/sites/${siteId}/equipment`, data, { params: { auto_verify: autoVerify } }).then(r => r.data),
  bulkCreateEquipment: (siteId: string, items: EquipmentIn[], autoVerify = true) =>
    api.post<EquipmentOut[]>(`/sites/${siteId}/equipment/bulk`, { items, auto_verify: autoVerify }).then(r => r.data),
  updateEquipment: (siteId: string, id: string, data: EquipmentIn) =>
    api.put<EquipmentOut>(`/sites/${siteId}/equipment/${id}`, data).then(r => r.data),
  deleteEquipment: (siteId: string, id: string) =>
    api.delete(`/sites/${siteId}/equipment/${id}`).then(() => undefined),
  verifyEquipment: (siteId: string, id: string) =>
    api.post<EquipmentOut>(`/sites/${siteId}/equipment/${id}/verify`).then(r => r.data),
  verifyAllEquipment: (siteId: string) =>
    api.post<EquipmentSummary>(`/sites/${siteId}/equipment/verify-all`).then(r => r.data),
  equipmentExportUrl: (siteId: string) => `/api/sites/${siteId}/equipment/export`,
}

// ── 의무 캘린더 ─────────────────────────────────

export const complianceApi = {
  listRules: (category?: string) =>
    api.get<ComplianceRuleOut[]>('/compliance/rules', { params: { category } }).then(r => r.data),

  listObligations: (siteId: string, params?: { category?: string; status?: string }) =>
    api.get<ObligationListOut>(`/compliance/sites/${siteId}/obligations`, { params }).then(r => r.data),

  upcomingForSite: (siteId: string, days = 30) =>
    api.get<UpcomingOut>(`/compliance/sites/${siteId}/obligations/upcoming`, { params: { days } }).then(r => r.data),

  upcomingForUser: (days = 30) =>
    api.get<UpcomingOut>('/compliance/upcoming', { params: { days } }).then(r => r.data),

  completeObligation: (siteId: string, obId: string, doneAt: string, notes?: string) =>
    api.post<ObligationOut>(`/compliance/sites/${siteId}/obligations/${obId}/complete`,
      { done_at: doneAt, notes }).then(r => r.data),

  recompute: (siteId: string) =>
    api.post(`/compliance/sites/${siteId}/recompute`).then(r => r.data),

  // 고도화: 인사이트
  riskSummary: (siteId: string) =>
    api.get<RiskSummaryOut>(`/compliance/sites/${siteId}/risk-summary`).then(r => r.data),
  roadmap: (siteId: string) =>
    api.get<RoadmapOut>(`/compliance/sites/${siteId}/roadmap`).then(r => r.data),
  forecast: (siteId: string) =>
    api.get<ForecastOut>(`/compliance/sites/${siteId}/forecast`).then(r => r.data),
  reportPdfUrl: (siteId: string) =>
    `/api/compliance/sites/${siteId}/report.pdf`,
}

// ── 교육 (관리자) ───────────────────────────────

export const trainingApi = {
  eduCodes: (siteId: string) =>
    api.get<ComplianceEduCode[]>(`/training/sites/${siteId}/edu-codes`).then(r => r.data),

  listSessions: (siteId: string) =>
    api.get<SessionOut[]>(`/training/sites/${siteId}/sessions`).then(r => r.data),
  createSession: (siteId: string, data: SessionIn) =>
    api.post<SessionOut>(`/training/sites/${siteId}/sessions`, data).then(r => r.data),
  getSession: (id: string) =>
    api.get<SessionOut>(`/training/sessions/${id}`).then(r => r.data),
  closeSession: (id: string) =>
    api.post<SessionOut>(`/training/sessions/${id}/close`).then(r => r.data),
  deleteSession: (id: string) =>
    api.delete(`/training/sessions/${id}`).then(() => undefined),
  listAttendance: (sessionId: string) =>
    api.get<AttendanceOut[]>(`/training/sessions/${sessionId}/attendance`).then(r => r.data),

  qrPngUrl: (sessionId: string) =>
    `${api.defaults.baseURL}/training/sessions/${sessionId}/qr.png`,
  attendanceSheetUrl: (sessionId: string) =>
    `${api.defaults.baseURL}/training/sessions/${sessionId}/attendance.pdf`,
  attendanceXlsxUrl: (sessionId: string) =>
    `${api.defaults.baseURL}/training/sessions/${sessionId}/attendance.xlsx`,
  certificateUrl: (sessionId: string, attendanceId: string) =>
    `${api.defaults.baseURL}/training/sessions/${sessionId}/attendance/${attendanceId}/certificate.pdf`,

  listWorkers: (siteId: string) =>
    api.get<WorkerOut[]>(`/training/sites/${siteId}/workers`).then(r => r.data),
  createWorker: (siteId: string, data: WorkerIn) =>
    api.post<WorkerOut>(`/training/sites/${siteId}/workers`, data).then(r => r.data),
  getWorker: (siteId: string, workerId: string) =>
    api.get<WorkerWithHistory>(`/training/sites/${siteId}/workers/${workerId}`).then(r => r.data),
  updateWorker: (siteId: string, workerId: string, data: WorkerIn) =>
    api.put<WorkerOut>(`/training/sites/${siteId}/workers/${workerId}`, data).then(r => r.data),
  deleteWorker: (siteId: string, workerId: string) =>
    api.delete(`/training/sites/${siteId}/workers/${workerId}`).then(() => undefined),

  coverage: (siteId: string) =>
    api.get<CoverageMatrixOut>(`/training/sites/${siteId}/coverage`).then(r => r.data),
}

// ── 교육 (게스트, 인증 없음) ────────────────────

export const trainingPublicApi = {
  info: (token: string) =>
    api.get<PublicSessionInfo>(`/training/public/${token}`).then(r => r.data),
  lookup: (token: string, phone: string) =>
    api.post<GuestLookupOut>(`/training/public/${token}/lookup`, { phone }).then(r => r.data),
  attend: (token: string, data: {
    name: string
    phone: string
    birth_year?: number | null
    department?: string | null
    job_type?: string | null
    employment_type: string
    position: string
    is_foreign: boolean
    native_language?: string | null
    privacy_consent: boolean
    /** 참석자 전자서명(dataURL png) — 시행규칙 §33 교육일지 필수. 없으면 출석부에 빈 서명란이 나온다. */
    signature?: string | null
  }) =>
    api.post<GuestAttendOut>(`/training/public/${token}/attend`, data).then(r => r.data),
  certUrl: (token: string, attendanceId: string) =>
    `${api.defaults.baseURL}/training/public/${token}/cert/${attendanceId}`,
  // Sprint 2 — QR 게스트 퀴즈 통합
  // Sprint 3 i18n — lang 파라미터 추가 (외국인 근로자 모국어 quiz)
  quizQuestions: (token: string, lang?: string) =>
    api.get<import('../types/sites').GuestQuizQuestionsOut>(
      `/training/public/${token}/quiz`,
      { params: lang ? { lang } : undefined },
    ).then(r => r.data),
  quizSubmit: (token: string, data: import('../types/sites').GuestQuizSubmitIn) =>
    api.post<import('../types/sites').GuestQuizSubmitOut>(
      `/training/public/${token}/quiz/submit`, data,
    ).then(r => r.data),
}

// Course 시리즈 기반 QR 출석 + 안전관리자 대시보드 (인증 필요)
export const courseQrApi = {
  createQrSession: (courseId: string, data: {
    site_id: string
    title?: string
    instructor?: string
    location?: string
    include_quiz?: boolean
    quiz_pass_score?: number
  }) =>
    api.post<import('../types/sites').CourseQrSessionCreateOut>(
      `/safety-education/course/${courseId}/qr-session`, data,
    ).then(r => r.data),
  attendanceSummary: (courseId: string) =>
    api.get<import('../types/sites').CourseAttendanceSummary>(
      `/safety-education/course/${courseId}/attendance-summary`,
    ).then(r => r.data),
}
